import {
    boot,
    keysOf,
    again,
    createEraroError,
    eraroCodesMap,
} from '@dazn/bootstrap-lib';

import { Configuration, InitializationModule } from '../global-modules';

import { getResourceStringLinkOverrides } from './getResourceStringLinkOverrides';

interface Feature {
    isEnabledAnonymousOptimizely: (
        experimentName: string,
        customAttrs?: boot.fflag.CustomAttributes | undefined,
    ) => boolean | null;
    getVariableStringAnonymousOptimizely: (
        experimentKey: string,
        variableKey: string,
        customAttrs?: boot.fflag.CustomAttributes | undefined,
    ) => string | null;
}

const replaceResourceStringLinks = (
    resourceStrings: boot.resourceStrings.Data,
    linksToUpdate: Record<string, string>,
) => {
    resourceStrings.Links = resourceStrings.Links.map(resourceStringData => {
        const { Key: key } = resourceStringData;
        const pagesToUpdate = keysOf(linksToUpdate);
        if (pagesToUpdate.includes(key)) {
            resourceStringData.Value = linksToUpdate[key];
        }
        return resourceStringData;
    });

    return resourceStrings;
};

interface InitGetResourceStringsParams {
    configuration: Configuration;
    fetch: boot.util.Fetch;
    getChapterCountry: () => boot.config.AnyCountry;
    getChapterLanguage: () => boot.config.Language;
    getPlatform: InitializationModule['getPlatform'];
    getResourceStringsEndpoint: () => string;
    features: Feature;
    getBrandName: () => boot.brand.BrandName;
    getCustomHeaders?: () => {
        isCustomHeadersEnabled?: boolean;
        brandName?: string;
        daznId?: string;
    };
}

/**
 *  @see [Request](../../../docs/api-call.md)
 */
const initGetResourceStrings = ({
    configuration,
    fetch,
    getChapterCountry,
    getChapterLanguage,
    getPlatform,
    getResourceStringsEndpoint,
    features: {
        isEnabledAnonymousOptimizely,
        getVariableStringAnonymousOptimizely,
    },
    getBrandName,
    getCustomHeaders,
}: InitGetResourceStringsParams) => {
    let cachedResult: boot.resourceStrings.Data;
    let cachedUrl: string;
    let cachedOverrides: string;

    const shouldReturnCache = (
        url: string,
        overrides: Record<string, unknown>,
    ) => cachedUrl === url && cachedOverrides === JSON.stringify(overrides);

    return () => {
        const featureName = 'f_ar_gernomio_cache_key';
        const nonceParam = isEnabledAnonymousOptimizely(featureName)
            ? `nonce=${getVariableStringAnonymousOptimizely(
                  featureName,
                  'nonce',
              )!}`
            : '';
        const brand =
            getBrandName() !== 'dazn' ? `brand=${getBrandName()}` : '';

        const queryString = [
            `region=${getChapterCountry()}`,
            `LanguageCode=${getChapterLanguage()}`,
            `Platform=${getPlatform()}`,
            nonceParam,
            brand,
        ]
            .filter(Boolean)
            .join('&');

        const url = `${getResourceStringsEndpoint()}?${queryString}`;

        const overrides = getResourceStringLinkOverrides(
            configuration,
            getChapterCountry(),
            getChapterLanguage(),
        );

        if (shouldReturnCache(url, overrides)) {
            /**
             * FIXME: there should be Promise.resolve, but it will break our public API
             * Issues may happen if in runtime configuration, country or language change
             * @see https://github.com/getndazn/auth-web-chapter/blob/c662b59d9f66d1a33ca25e011b52ba890acd3a90/src/bootstrap/bootstrap.js#L52
             */
            return cachedResult;
        }

        const generateResourceStringsError = (
            message: string,
            internalCode: string,
            error?: Error,
        ) =>
            createEraroError(
                `Failed to get resource strings data: ${message}`,
                eraroCodesMap.category.ResourceStringService,
                eraroCodesMap.code.GenericError,
                internalCode,
                error,
            );

        const fetchResourceStrings = () => {
            const fetchOptions: RequestInit = {};

            // Add custom common headers if enabled
            if (getCustomHeaders) {
                const customHeaders = getCustomHeaders();
                if (customHeaders?.isCustomHeadersEnabled) {
                    const headers: Record<string, string> = {};
                    if (customHeaders.brandName) {
                        headers['X-BRAND'] = customHeaders.brandName;
                    }
                    if (customHeaders.daznId) {
                        headers['X-DAZNID'] = customHeaders.daznId;
                    }
                    if (Object.keys(headers).length > 0) {
                        fetchOptions.headers = headers;
                    }
                }
            }

            return fetch(url, fetchOptions)
                .catch(error => {
                    throw generateResourceStringsError(
                        `Failed to fetch ${url}`,
                        eraroCodesMap.internal.NetworkError,
                        error,
                    );
                })
                .then(response => {
                    if (!response.ok) {
                        const { status, statusText } = response;
                        throw generateResourceStringsError(
                            `${status} ${statusText} from ${url}`,
                            String(status),
                        );
                    }

                    return response.json().catch((error: Error) => {
                        throw generateResourceStringsError(
                            'Failed to parse json',
                            eraroCodesMap.internal.ParserError,
                            error,
                        );
                    });
                })
                .then(resourceStrings =>
                    replaceResourceStringLinks(resourceStrings, overrides),
                )
                .then(result => {
                    cachedResult = result;
                    cachedUrl = url;
                    cachedOverrides = JSON.stringify(overrides);
                    return result;
                });
        };

        return again(fetchResourceStrings);
    };
};
export { initGetResourceStrings };
